import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '@/auth/AuthContext';
import { toast } from 'sonner';

// AuthCallback — silently exchanges the URL-fragment `session_id` from
// Emergent's Google OAuth for our own bearer `session_token`, then routes
// the user directly into the dashboard.
//
// React StrictMode double-invokes effects, and any race between checking
// the URL fragment and calling /api/auth/google/session would burn the
// one-time `session_id`. We guard with a `useRef` flag set synchronously
// at the start of the effect — exactly the pattern the playbook calls out.
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { applyToken } = useAuth();
  const guard = useRef(false);
  const [msg, setMsg] = useState('Completing sign-in…');

  useEffect(() => {
    if (guard.current) return;
    guard.current = true;
    const hash = location.hash || window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate('/', { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(m[1]);
    // Clear the fragment immediately so a refresh doesn't reattempt.
    if (window.history?.replaceState) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    (async () => {
      try {
        const r = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
        const { session_token } = r.data;
        await applyToken(session_token);
        // Successful sign-in goes straight into the app — no extra steps.
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail || 'Sign-in failed';
        toast.error(detail);
        navigate('/', { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background" data-testid="auth-callback">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent mb-4" />
        <p className="text-sm text-muted-foreground">{msg}</p>
      </div>
    </div>
  );
}
